import { useEffect, useRef, useState } from "react";
import { type ShiftFormErrors, type ShiftFormNames, type ShiftFormValues, validateShiftForm } from "./shift-form";

export type ShiftFormState = {
  values: ShiftFormValues;
  errors: ShiftFormErrors;
  names: ShiftFormNames;
  change: (patch: Partial<ShiftFormValues>) => void;
  setName: (patch: ShiftFormNames) => void;
  /** Checks the form; true when it can be saved. Errors show from then on and follow what is entered. */
  validate: () => boolean;
};

/**
 * The shift form's values, starting from `initialValues` and going back to them whenever `resetKey` changes
 * (a dialog opening, a page loading another shift). Only that resets it, so a host passing a fresh prefill
 * object on every render does not wipe what is being typed.
 */
export const useShiftFormState = (initialValues: ShiftFormValues, resetKey: unknown): ShiftFormState => {
  const [values, setValues] = useState(initialValues);
  const [names, setNames] = useState<ShiftFormNames>({});
  const [errors, setErrors] = useState<ShiftFormErrors>({});
  const attempted = useRef(false);
  const prefill = useRef(initialValues);
  prefill.current = initialValues;

  useEffect(() => {
    setValues(prefill.current);
    setNames({});
    setErrors({});
    attempted.current = false;
  }, [resetKey]);

  return {
    values,
    errors,
    names,
    change: (patch) => {
      const next = { ...values, ...patch };
      setValues(next);
      if (attempted.current) setErrors(validateShiftForm(next));
    },
    setName: (patch) => setNames((current) => ({ ...current, ...patch })),
    validate: () => {
      attempted.current = true;
      const found = validateShiftForm(values);
      setErrors(found);
      return Object.keys(found).length === 0;
    },
  };
};
